import { z } from 'zod';
import type { ParseResult, SchemaIssue } from '../voyage/schemas.js';
import { decIsZero, decSum, formatDec } from './decimal.js';
import { optEnum, optId, optNote, optSigned, requiredIssues, resultOf, typed } from './fields.js';
import type { LedgerLine } from './ledger.js';

/**
 * ROB düzeltme talebi (ADR 0117 §4, Ek Y4; ortak belgesi §10 "Mutabakat ve Düzeltme"): ölçümle
 * hesaplanan arasındaki fark yalnız bu talep onaylanınca deftere girer. Gemi verisidir; onay
 * matrisinde gemi adımı ve ofis adımı (şirket onaylayıcısı). Son onay buluttan inince gemi
 * sunucusu `adjustment` hareketini kendiliğinden yazar (ADR 0104 kalıbı). Satırlar tank başına
 * işaretli MT; sıfır satır deftere girmez. Düzeltme tüketim değildir (MRV'de yanma sayılmaz).
 * Saf modül.
 */

/** Fark nedeni (ekran 06 "Fark Nedeni"). */
export const ADJUSTMENT_REASONS = [
  'measurement_tolerance',
  'measurement_error',
  'calibration',
  'density',
  'temperature',
  'unrecorded_consumption',
  'sludge_water',
  'bunker_difference',
  'other',
] as const;
export type AdjustmentReason = (typeof ADJUSTMENT_REASONS)[number];

export const ADJUSTMENT_LINES_MAX = 40;

const lineSchema = z.strictObject({
  tankId: z.uuid('INVALID_ID'),
  fuelTypeId: optId,
  /** İşaretli MT: eksi defteri azaltır, artı artırır. */
  massMt: optSigned(),
});

export const fuelAdjustmentSchema = z.strictObject({
  soundingId: optId,
  reasonCode: optEnum(ADJUSTMENT_REASONS),
  description: optNote(1000),
  lines: z
    .array(lineSchema, typed('INVALID_LIST'))
    .max(ADJUSTMENT_LINES_MAX, 'TOO_LONG')
    .nullish()
    .transform((value) => value ?? []),
});
export type FuelAdjustmentInput = z.output<typeof fuelAdjustmentSchema>;

export function parseFuelAdjustment(input: unknown): ParseResult<FuelAdjustmentInput> {
  return resultOf(fuelAdjustmentSchema.safeParse(input));
}

/** Toplam düzeltme (ekran 06 "Düzeltme Miktarı (MT)"). */
export function adjustmentTotal(lines: FuelAdjustmentInput['lines']): string {
  return formatDec(decSum(lines.map((l) => l.massMt ?? '0')));
}

/**
 * Gönderim sorunları: ölçüm ve neden zorunlu; "diğer" neden ya da sıfırdan farklı düzeltme
 * açıklama ister; satırda yakıt ve miktar; aynı tank iki satır olmaz.
 */
export function adjustmentSubmitIssues(value: FuelAdjustmentInput): SchemaIssue[] {
  const issues = requiredIssues(value, ['soundingId', 'reasonCode']);
  const moves = value.lines.some((l) => l.massMt !== null && !decIsZero(l.massMt));
  if (value.description === null && (value.reasonCode === 'other' || moves)) {
    issues.push({ path: 'description', code: 'REQUIRED' });
  }
  const seen = new Set<string>();
  value.lines.forEach((line, index) => {
    if (line.fuelTypeId === null)
      issues.push({ path: `lines.${index}.fuelTypeId`, code: 'REQUIRED' });
    if (line.massMt === null) issues.push({ path: `lines.${index}.massMt`, code: 'REQUIRED' });
    if (seen.has(line.tankId)) issues.push({ path: `lines.${index}.tankId`, code: 'DUPLICATE' });
    seen.add(line.tankId);
  });
  return issues;
}

/** Düzeltmenin defter satırları: tank başına bir `adjustment`; sıfır satır yazılmaz. */
export function adjustmentPosting(lines: FuelAdjustmentInput['lines']): LedgerLine[] {
  return lines
    .filter((line) => line.massMt !== null && line.fuelTypeId !== null && !decIsZero(line.massMt))
    .map((line) => ({
      kind: 'adjustment',
      tankId: line.tankId,
      fuelTypeId: line.fuelTypeId as string,
      batchId: null,
      massMt: formatDec(line.massMt as string),
      lineKey: `tank:${line.tankId}`,
      density15: null,
    }));
}
